import { supabase } from '@/lib/supabase';

export interface HonestBacktestConfig {
  fileId: string;
  startPosition: number;
  endPosition?: number;
  patternRefreshFrequency: number;
  winThreshold: number;
  minSequenceLength?: number;
  maxSequenceLength?: number;
  // Rolling window options (defaults ensure backward compatibility)
  useRollingWindow?: boolean;
  windowSize?: number;
  fixedPatternLength?: number;
  minFrequency?: number;
  minMatches?: number;
  returnDebug?: boolean;
  // TP/SL ratios for realistic growth calculation
  tpRatio?: number;
  slRatio?: number;
}

export interface HonestBacktestPosition {
  position: number;
  prediction: 'WIN' | 'LOSS' | 'SKIP';
  actualResult: number;
  confidence: number;
  currentPatternCount: number;
  usedPatterns: number;
  newPatternsAdded: number;
  patternBreakdown?: {
    patternsUsed: Array<{
      pattern: string;
      frequency: number;
      successRate: number;
      confidenceScore: number;
    }>;
  };
  skipReason?: string;
  winProbability?: number;
  lossProbability?: number;
  // Compact pattern sample for history storage (top 2 patterns)
  patternsSample?: Array<{
    pattern: string;
    frequency: number;
    successRate: number;
    confidenceScore: number;
  }>;
  // Rolling window debug info (only when returnDebug=true)
  debugInfo?: {
    windowStart?: number;
    windowEnd?: number;
    windowSize?: number;
    patternsInWindow?: number;
    matchingPatterns?: number;
    averageConfidence?: number;
  };
}

export interface HonestBacktestResult {
  config: HonestBacktestConfig;
  positions: HonestBacktestPosition[];
  summary: {
    totalPositions: number;
    predictedPositions: number;
    skippedPositions: number;
    correctPredictions: number;
    accuracy: number;
    skipRate: number;
    finalPatternCount: number;
    initialPatternCount: number;
    averagePatternsPerPosition: number;
    maxPatterns: number;
    minPatterns: number;
  };
  patternEvolution: Array<{
    position: number;
    patternCount: number;
    newPatterns: number;
    updatedPatterns: number;
  }>;
  processingTime: number;
}

export interface StoredHonestBacktestSimulation {
  id: string;
  file_id: string;
  user_id: string;
  start_position: number;
  end_position?: number;
  pattern_refresh_frequency: number;
  win_threshold: number;
  total_positions: number;
  predicted_positions: number;
  skipped_positions: number;
  correct_predictions: number;
  accuracy: number;
  skip_rate: number;
  positions_data: HonestBacktestPosition[];
  pattern_evolution: Array<{
    position: number;
    patternCount: number;
    newPatterns: number;
    updatedPatterns: number;
  }>;
  processing_time_ms: number;
  summary_stats: {
    finalPatternCount: number;
    initialPatternCount: number;
    averagePatternsPerPosition: number;
    maxPatterns: number;
    minPatterns: number;
  };
  config?: {
    useRollingWindow?: boolean;
    windowSize?: number;
    fixedPatternLength?: number;
    minFrequency?: number;
    minMatches?: number;
    returnDebug?: boolean;
  };
  created_at: string;
  updated_at: string;
}

export class HonestBacktestService {
  static async runBacktest(
    config: HonestBacktestConfig,
    onProgress?: (progress: { current: number; total: number; status: string }) => void
  ): Promise<{ success: boolean; result?: HonestBacktestResult; error?: string }> {
    try {
      // Ensure backward compatibility with defaults
      const configWithDefaults = {
        ...config,
        useRollingWindow: config.useRollingWindow ?? false,
        windowSize: config.windowSize ?? 50,
        minFrequency: config.minFrequency ?? 1,
        minMatches: config.minMatches ?? 1,
        returnDebug: config.returnDebug ?? false
      };

      const { data, error } = await supabase.functions.invoke('honest-progressive-backtest', {
        body: { config: configWithDefaults }
      });

      if (error) {
        console.error('Edge function error:', error);
        return { success: false, error: error.message };
      }

      if (!data?.result) {
        return { success: false, error: 'No result returned from backtest' };
      }

      // Store the result in database
      const user = await supabase.auth.getUser();
      if (user.data.user) {
        await this.storeSimulation(user.data.user.id, data.result);
      }

      return { success: true, result: data.result };
    } catch (error) {
      console.error('Error running honest backtest:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  static async storeSimulation(userId: string, result: HonestBacktestResult): Promise<void> {
    try {
      // Note: The edge function already stores the simulation and patterns
      // This method is kept for compatibility but the actual storage happens in the edge function
      console.log('Simulation storage handled by edge function');
    } catch (error) {
      console.error('Error in storeSimulation:', error);
    }
  }

  static async getSimulations(fileId: string): Promise<{ 
    success: boolean; 
    data?: StoredHonestBacktestSimulation[]; 
    error?: string 
  }> {
    try {
      const { data, error } = await supabase
        .from('honest_backtest_simulations')
        .select('*')
        .eq('file_id', fileId)
        .order('created_at', { ascending: false });

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching honest backtest simulations:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  static async getSimulationDetail(simulationId: string): Promise<{ 
    success: boolean; 
    data?: StoredHonestBacktestSimulation; 
    error?: string 
  }> {
    try {
      const { data, error } = await supabase
        .from('honest_backtest_simulations')
        .select('*')
        .eq('id', simulationId)
        .single();

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, data };
    } catch (error) {
      console.error('Error fetching honest backtest simulation detail:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  static async deleteSimulation(simulationId: string): Promise<{ 
    success: boolean; 
    error?: string 
  }> {
    try {
      // Delete patterns first (foreign key constraint)
      await supabase
        .from('honest_backtest_patterns')
        .delete()
        .eq('simulation_id', simulationId);

      // Then delete simulation
      const { error } = await supabase
        .from('honest_backtest_simulations')
        .delete()
        .eq('id', simulationId);

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true };
    } catch (error) {
      console.error('Error deleting honest backtest simulation:', error);
      return { 
        success: false, 
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  }

  // Get patterns from a specific simulation for comparison
  static async getSimulationPatterns(simulationId: string): Promise<{ success: boolean; data?: any[]; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('honest_backtest_patterns')
        .select('*')
        .eq('simulation_id', simulationId)
        .order('success_rate', { ascending: false });

      if (error) {
        throw error;
      }

      return { success: true, data: data || [] };
    } catch (error: any) {
      console.error('Error fetching simulation patterns:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to fetch simulation patterns' 
      };
    }
  }

  // Compare patterns between honest backtest and real-time prediction
  static async comparePatterns(simulationId: string, fileId: string): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      // Get honest backtest patterns
      const honestPatterns = await this.getSimulationPatterns(simulationId);
      if (!honestPatterns.success) {
        return honestPatterns;
      }

      // Get real-time prediction patterns  
      const { data: realtimePatterns, error: realtimeError } = await supabase
        .from('trading_patterns')
        .select('*')
        .eq('file_id', fileId)
        .order('success_rate', { ascending: false });

      if (realtimeError) {
        throw realtimeError;
      }

      // Create comparison analysis
      const comparison = {
        honest_backtest: {
          total_patterns: honestPatterns.data?.length || 0,
          patterns: honestPatterns.data || []
        },
        realtime_prediction: {
          total_patterns: realtimePatterns?.length || 0,
          patterns: realtimePatterns || []
        },
        differences: this.analyzePatternDifferences(honestPatterns.data || [], realtimePatterns || [])
      };

      return { success: true, data: comparison };
    } catch (error: any) {
      console.error('Error comparing patterns:', error);
      return { 
        success: false, 
        error: error.message || 'Failed to compare patterns' 
      };
    }
  }

  // Analyze differences between honest backtest and realtime patterns
  private static analyzePatternDifferences(honestPatterns: any[], realtimePatterns: any[]) {
    const honestMap = new Map(honestPatterns.map(p => [p.pattern, p]));
    const realtimeMap = new Map(realtimePatterns.map(p => [p.pattern, p]));
    
    const analysis = {
      common_patterns: [] as any[],
      honest_only: [] as any[],
      realtime_only: [] as any[],
      success_rate_differences: [] as any[]
    };

    // Find common patterns and differences
    for (const [pattern, honestData] of honestMap) {
      if (realtimeMap.has(pattern)) {
        const realtimeData = realtimeMap.get(pattern);
        analysis.common_patterns.push({
          pattern,
          honest: honestData,
          realtime: realtimeData,
          success_rate_diff: Math.abs(honestData.success_rate - realtimeData.success_rate)
        });
        
        if (Math.abs(honestData.success_rate - realtimeData.success_rate) > 10) {
          analysis.success_rate_differences.push({
            pattern,
            honest_success_rate: honestData.success_rate,
            realtime_success_rate: realtimeData.success_rate,
            difference: honestData.success_rate - realtimeData.success_rate
          });
        }
      } else {
        analysis.honest_only.push(honestData);
      }
    }

    // Find realtime-only patterns
    for (const [pattern, realtimeData] of realtimeMap) {
      if (!honestMap.has(pattern)) {
        analysis.realtime_only.push(realtimeData);
      }
    }

    return analysis;
  }

  static validateConfig(config: Partial<HonestBacktestConfig>): { isValid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.fileId) {
      errors.push('File ID is required');
    }

    if (typeof config.startPosition !== 'number' || config.startPosition < 10) {
      errors.push('Start position must be at least 10');
    }

    if (typeof config.patternRefreshFrequency !== 'number' || config.patternRefreshFrequency < 1) {
      errors.push('Pattern refresh frequency must be at least 1');
    }

    if (typeof config.winThreshold !== 'number' || config.winThreshold < 0.5 || config.winThreshold > 1) {
      errors.push('Win threshold must be between 0.5 and 1.0');
    }

    // Rolling window validation
    if (config.useRollingWindow) {
      if (typeof config.windowSize !== 'number' || config.windowSize < 10) {
        errors.push('Window size must be at least 10 when using rolling window');
      }

      if (config.fixedPatternLength && (typeof config.fixedPatternLength !== 'number' || config.fixedPatternLength < 2 || config.fixedPatternLength > 10)) {
        errors.push('Fixed pattern length must be between 2 and 10');
      }

      if (typeof config.minFrequency !== 'number' || config.minFrequency < 1) {
        errors.push('Minimum frequency must be at least 1');
      }

      if (typeof config.minMatches !== 'number' || config.minMatches < 1) {
        errors.push('Minimum matches must be at least 1');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static exportToCSV(result: HonestBacktestResult): string {
    const headers = [
      'Position',
      'Prediction',
      'Actual Result',
      'Confidence',
      'Win Probability',
      'Pattern Count',
      'Used Patterns',
      'New Patterns',
      'Skip Reason',
      'Is Correct'
    ];

    const rows = result.positions.map(pos => [
      pos.position,
      pos.prediction,
      pos.actualResult,
      pos.confidence.toFixed(4),
      pos.winProbability?.toFixed(4) || '',
      pos.currentPatternCount,
      pos.usedPatterns,
      pos.newPatternsAdded,
      pos.skipReason || '',
      pos.prediction === 'SKIP' ? 'N/A' : 
        (pos.prediction === 'WIN' && pos.actualResult === 1) || 
        (pos.prediction === 'LOSS' && pos.actualResult === 0) ? 'CORRECT' : 'WRONG'
    ]);

    return [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
  }
}